/**
 * Module-level registry that maps shift identifiers (the `external_driver_id`
 * we generate when building the API payload, or the vehicle's
 * `external_reference_id`) to a human-readable label such as "DAY 1 COACH".
 *
 * The simulation API does not currently accept a `shift_name` field on the
 * shift payload, so we keep the label on the client. When the activities come
 * back from `/run/`, the results panel looks up the label by the shift / driver
 * / vehicle id that the API echoes (`vehicle_shift_id`, `shift_id`,
 * `vehicle_id`).
 *
 * The registry is intentionally process-wide and never cleared automatically —
 * it grows once per simulation run and is keyed by UUID, so collisions are not
 * a concern. Tests can use `clearShiftLabelRegistry()` for isolation.
 */

const SHIFT_LABEL_REGISTRY = new Map<string, string>();

/**
 * Per-shift route metadata, registered alongside the shift label but kept
 * separate so the *vehicle* label (`shift_name` / `vehicle_name` — typically
 * the rego) and the *route* name (e.g. "DAY 1") can diverge. Slot-driven flows
 * leave the vehicle name as the rego (so the right physical vehicle is matched
 * by id on upload) while the route the simulator builds is labelled by band +
 * ordinal. Stamped onto activities as `route_name` / `shift_band`, where the
 * Fixed Route export's `resolveShiftNaming` reads them.
 */
export interface ShiftRouteMeta {
  routeName?: string;
  shiftBand?: string;
}

const SHIFT_ROUTE_META_REGISTRY = new Map<string, ShiftRouteMeta>();

/**
 * Register a label under one or more shift identifiers. Empty / non-string
 * keys and empty labels are ignored.
 */
export function registerShiftLabel(keys: Array<string | null | undefined>, label: string | null | undefined): void {
  if (typeof label !== 'string') return;
  const trimmedLabel = label.trim();
  if (trimmedLabel === '') return;
  for (const key of keys) {
    if (typeof key !== 'string') continue;
    const trimmedKey = key.trim();
    if (trimmedKey === '') continue;
    SHIFT_LABEL_REGISTRY.set(trimmedKey, trimmedLabel);
  }
}

/**
 * Look up a label by any one of several candidate keys (e.g. the activity's
 * `vehicle_shift_id`, `shift_id`, `vehicle_id`). Returns the first non-null
 * match, or `null` if nothing is registered.
 */
export function lookupShiftLabel(keys: Array<string | null | undefined>): string | null {
  for (const key of keys) {
    if (typeof key !== 'string') continue;
    const trimmed = key.trim();
    if (trimmed === '') continue;
    const hit = SHIFT_LABEL_REGISTRY.get(trimmed);
    if (hit) return hit;
  }
  return null;
}

/**
 * Register route metadata (route name and/or shift band) under one or more
 * shift identifiers — the same external ids used for {@link registerShiftLabel}.
 * Fields with empty / non-string values are dropped; a key gets an entry only
 * if at least one field survives. Re-registering merges (last write wins
 * per field).
 */
export function registerRouteMeta(keys: Array<string | null | undefined>, meta: ShiftRouteMeta): void {
  const routeName = typeof meta.routeName === 'string' && meta.routeName.trim() !== '' ? meta.routeName.trim() : undefined;
  const shiftBand = typeof meta.shiftBand === 'string' && meta.shiftBand.trim() !== '' ? meta.shiftBand.trim() : undefined;
  if (!routeName && !shiftBand) return;
  for (const key of keys) {
    if (typeof key !== 'string') continue;
    const trimmedKey = key.trim();
    if (trimmedKey === '') continue;
    const existing = SHIFT_ROUTE_META_REGISTRY.get(trimmedKey);
    SHIFT_ROUTE_META_REGISTRY.set(trimmedKey, {
      routeName: routeName ?? existing?.routeName,
      shiftBand: shiftBand ?? existing?.shiftBand,
    });
  }
}

/** Look up route metadata by any of several candidate keys; first match wins. */
export function lookupRouteMeta(keys: Array<string | null | undefined>): ShiftRouteMeta | null {
  for (const key of keys) {
    if (typeof key !== 'string') continue;
    const trimmed = key.trim();
    if (trimmed === '') continue;
    const hit = SHIFT_ROUTE_META_REGISTRY.get(trimmed);
    if (hit) return hit;
  }
  return null;
}

/** Test helper — clears every registered label and route meta. Not for production. */
export function clearShiftLabelRegistry(): void {
  SHIFT_LABEL_REGISTRY.clear();
  SHIFT_ROUTE_META_REGISTRY.clear();
}

// ---------------------------------------------------------------------------
// Persistent vehicle-label carrier (encoded into the echoed `brand` field)
// ---------------------------------------------------------------------------

/**
 * The shift-label / route-meta registries above are in-memory only, so the
 * names they hold are lost on reopen / reload / a subsequent run's reset — even
 * though the simulator faithfully echoes the vehicle's *ids*. To make the names
 * survive, we additionally smuggle them through `vehicle.brand`, a free-text
 * `String` column HTTM persists and echoes on `/results/{id}/vehicles` but never
 * reads (the optimiser ignores it). `vehicle_type` is left alone — it is read by
 * `resultsVehiclesToFleet`. The value is a JSON object tagged with `_cl` so a
 * genuine brand string is never mistaken for an encoded label.
 */
const BRAND_LABEL_MARKER = '_cl';

export interface VehicleBrandLabel {
  name?: string;
  routeName?: string;
  shiftBand?: string;
}

/**
 * Encode a vehicle label / route meta into a `brand` string, or return `null`
 * when there is nothing to carry (caller should keep its existing brand value).
 */
export function encodeVehicleBrandLabel(meta: VehicleBrandLabel): string | null {
  const name = typeof meta.name === 'string' && meta.name.trim() !== '' ? meta.name.trim() : undefined;
  const routeName =
    typeof meta.routeName === 'string' && meta.routeName.trim() !== '' ? meta.routeName.trim() : undefined;
  const shiftBand =
    typeof meta.shiftBand === 'string' && meta.shiftBand.trim() !== '' ? meta.shiftBand.trim() : undefined;
  if (!name && !routeName && !shiftBand) return null;
  return JSON.stringify({ [BRAND_LABEL_MARKER]: 1, n: name, r: routeName, b: shiftBand });
}

/** Decode a `brand` string produced by {@link encodeVehicleBrandLabel}; `null` if it isn't one. */
export function decodeVehicleBrandLabel(brand: string | null | undefined): VehicleBrandLabel | null {
  if (typeof brand !== 'string' || !brand.includes(BRAND_LABEL_MARKER)) return null;
  try {
    const parsed = JSON.parse(brand) as Record<string, unknown>;
    if (!parsed || parsed[BRAND_LABEL_MARKER] !== 1) return null;
    const pick = (v: unknown) => (typeof v === 'string' && v.trim() !== '' ? v.trim() : undefined);
    const out: VehicleBrandLabel = { name: pick(parsed.n), routeName: pick(parsed.r), shiftBand: pick(parsed.b) };
    return out.name || out.routeName || out.shiftBand ? out : null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Activity stamping (server-id ↔ external-id join)
// ---------------------------------------------------------------------------

/**
 * Minimal vehicle-row shape we need to bridge simulator-generated ids to the
 * external ids we sent with the fleet payload. Mirrors `ResultsVehicleRow` from
 * `types/simulation.ts`; declared loosely here to avoid a circular import.
 */
export interface ShiftLabelVehicleRow {
  vehicle_id?: string;
  vehicle_shift_id?: string;
  external_reference_id?: string | null;
  external_driver_id?: string | null;
  /** Carries the encoded vehicle label / route meta (see {@link encodeVehicleBrandLabel}). */
  brand?: string | null;
}

interface ActivityLike {
  vehicle_id?: string;
  vehicle_shift_id?: string;
  shift_id?: string;
  shift_name?: string | null;
  vehicle_name?: string | null;
  route_name?: string | null;
  shift_band?: string | null;
  [key: string]: unknown;
}

/**
 * Stamp `shift_name` / `vehicle_name` onto activities by joining their
 * simulator-generated `vehicle_id`/`vehicle_shift_id` to the `/results/{id}/vehicles`
 * rows (which carry our outbound `external_reference_id` / `external_driver_id`)
 * and looking up the label in the shift label registry.
 *
 * Round 1's lookup chain on the render site was correct in spirit but missed
 * because the registration ids were never seen by the API. This join repairs
 * the link by going through `/vehicles`, which DOES echo our external ids.
 *
 * If an activity already carries a non-empty `shift_name`, it's preserved
 * (API-echoed values win). Returns a NEW array; input is not mutated.
 *
 * Label source per vehicle row: the in-memory registry first (it can be more
 * specific — e.g. per-shift `#1`/`#2` labels on a multi-shift vehicle), then the
 * decoded `brand` field as a fallback. The registry is empty after a reopen /
 * reload, so `brand` — which HTTM persists and echoes — is what makes the names
 * survive there.
 */
export function stampShiftNamesOnActivities<T extends ActivityLike>(
  activities: T[] | null | undefined,
  vehicleRows: ShiftLabelVehicleRow[] | null | undefined,
): T[] {
  if (!Array.isArray(activities) || activities.length === 0) return activities ?? [];
  if (!Array.isArray(vehicleRows) || vehicleRows.length === 0) return activities;

  // Build a label and route-meta per simulator-side id by joining
  // vehicleRows → registry (then the encoded `brand` as a persistent fallback).
  // `vehicle_id` and `vehicle_shift_id` (sim-generated) → label / { routeName, shiftBand }.
  const labelByServerId = new Map<string, string>();
  const routeMetaByServerId = new Map<string, ShiftRouteMeta>();
  for (const row of vehicleRows) {
    const externalKeys = [row.external_driver_id, row.external_reference_id];
    const brand = decodeVehicleBrandLabel(row.brand);
    const label = lookupShiftLabel(externalKeys) ?? brand?.name ?? null;
    const registryMeta = lookupRouteMeta(externalKeys);
    const routeMeta: ShiftRouteMeta | null =
      registryMeta ??
      (brand && (brand.routeName || brand.shiftBand)
        ? { routeName: brand.routeName, shiftBand: brand.shiftBand }
        : null);
    if (!label && !routeMeta) continue;
    const serverIds = [row.vehicle_shift_id, row.vehicle_id];
    serverIds.forEach((serverId, idx) => {
      if (typeof serverId !== 'string' || serverId.trim() === '') return;
      const key = serverId.trim();
      // First write wins for the vehicle-level id (idx 1) so a more specific
      // shift-level id (idx 0) isn't clobbered by a later vehicle-level row.
      const isVehicleLevel = idx === 1;
      if (label && !(isVehicleLevel && labelByServerId.has(key))) {
        labelByServerId.set(key, label);
      }
      if (routeMeta && !(isVehicleLevel && routeMetaByServerId.has(key))) {
        routeMetaByServerId.set(key, routeMeta);
      }
    });
  }

  if (labelByServerId.size === 0 && routeMetaByServerId.size === 0) return activities;

  return activities.map((activity) => {
    const candidates = [activity.vehicle_shift_id, activity.shift_id, activity.vehicle_id];
    let next: T | null = null;
    const ensureCopy = (): T => (next ??= { ...activity } as T);

    // Shift / vehicle label (preserve any API-echoed value).
    if (!(typeof activity.shift_name === 'string' && activity.shift_name.trim() !== '')) {
      for (const candidate of candidates) {
        if (typeof candidate !== 'string' || candidate.trim() === '') continue;
        const label = labelByServerId.get(candidate.trim());
        if (label) {
          const copy = ensureCopy();
          copy.shift_name = label;
          if (!(typeof copy.vehicle_name === 'string' && copy.vehicle_name.trim() !== '')) {
            copy.vehicle_name = label;
          }
          break;
        }
      }
    }

    // Route name / shift band (preserve any API-echoed value per field).
    for (const candidate of candidates) {
      if (typeof candidate !== 'string' || candidate.trim() === '') continue;
      const meta = routeMetaByServerId.get(candidate.trim());
      if (!meta) continue;
      const hasRoute = typeof activity.route_name === 'string' && activity.route_name.trim() !== '';
      const hasBand = typeof activity.shift_band === 'string' && activity.shift_band.trim() !== '';
      if (meta.routeName && !hasRoute) ensureCopy().route_name = meta.routeName;
      if (meta.shiftBand && !hasBand) ensureCopy().shift_band = meta.shiftBand;
      break;
    }

    return next ?? activity;
  });
}
